import {
  faBell,
  faFileHeart,
  faListCheck
} from '@fortawesome/pro-solid-svg-icons'

import { FilterFieldSchema } from '@emmacare/common/services/pageLogger/types'

import {
  ACTION_TYPE,
  ASSIGNEE,
  CARE_GAP_TYPE,
  CASE_MANAGER_UUID,
  COMPANY_DIVISION,
  CREATED_BY,
  CREATED_DATE_FROM,
  CREATED_DATE_TO,
  DUE_DATE_FROM,
  DUE_DATE_TO,
  FACILITY,
  FIRST_NAME,
  GROUP,
  GROUP_BY,
  INSURANCE_TYPE_UUID,
  IS_NO_DUE_DATE,
  IS_RPM_BILLABLE,
  LAST_ACTIVITY_DATE_FROM,
  LAST_ACTIVITY_DATE_TO,
  LAST_NAME,
  MENTIONED_USERS,
  NOTIFICATION_TYPE,
  PATIENT_ID,
  PATIENT_REQUESTED_APPOINTMENT,
  PAYER,
  PRIORITY_FIELD_NAME,
  PROVIDER,
  REFERRAL_SOURCE,
  STATUS,
  TASK_TYPE,
  UUID
} from 'src/constants'
import { addAllValue } from 'src/utils/v5/DataTrees'

// routes
export const ACTION_HUB = '/company/tasks'
export const ACTION_HUB_EXPORT = '/company/tasks/export'
export const ACTION_HUB_SUMMARY = '/company/tasks/summary'
export const ACTION_HUB_GROUP_BY = '/company/tasks/groupBy'
export const ACTION_HUB_ASSIGNEE_FILTER_LIST_GET =
  '/company/tasks/assigneeFilterList'
export const ACTION_HUB_ASSIGNEE_LIST = '/company/tasks/assigneeList'

export const ACTION_GET_PATCH_DELETE = '/company/task/:actionUuid'
export const ACTION_POST = '/company/task'

export const ACTION_ATTACHMENT_PATCH = '/company/task/:actionUuid/attachments'

export const ACTION_COMMENT_POST = '/company/task/:actionUuid/comment'
export const ACTION_COMMENT_DELETE = '/company/taskComment/:actionCommentUuid'
export const ACTION_COMMENTS_GET = '/company/task/:actionUuid/comments'

export const ACTION_HISTORY_GET = '/company/taskHistory/:actionUuid'

export const ACTION_CARE_GAP_TYPES_GET = '/company/payer/:payerUuid/careGapType'
export const ACTION_CARE_GAP_TYPES_LIST_GET = '/company/careGapType'
export const ACTION_CARE_GAP_ATTACHMENT_GET_DELETE =
  '/company/careGap/:actionUuid/attachment/:careGapCustomFieldValueUuid'

// messages
export const ACTION_SAVE_MESSAGE = 'Action has been successfully saved!'
export const ACTION_DELETE_MESSAGE = 'Action has been successfully deleted!'
export const ACTION_CONFIRM_DELETE_MESSAGE = 'Do you want to delete the action?'
export const ACTION_DISABLED_CLOSE_MESSAGE =
  'You are not the creator of the action or haven’t been assigned to it yet.'
export const ACTION_DISABLED_DELETE_MESSAGE =
  'The action can’t be deleted as there are notes left in the action thread.'
export const ACTION_COMMENT_SAVE_MESSAGE =
  'Action comment has been successfully saved!'
export const ACTION_COMMENT_DELETE_MESSAGE =
  'Action comment has been successfully deleted!'
export const ACTION_APPOINTMENT_WARNING_MESSAGE =
  'Set-up a clinician for the patient to create an appointment.'
export const ACTION_NO_ACCESS_MESSAGE =
  "The user doesn't have access to this task anymore."
export const NOTIFICATION_WARNING_MESSAGE =
  'Only notification recipient can acknowledge the notification.'
export const ACTION_REDIRECT_MESSAGE = 'Go to Actions.'

// sorting
export const ACTION_HUB_DEFAULT_SORT = {
  sortedColumn: 'lastActivity',
  sortedColumnOrder: 'descend'
}
export const ACTION_DEFAULT_SORT = {
  sortedColumn: 'createdAt',
  sortedColumnOrder: 'descend'
}

export const ACTION_HUB_GROUP_BY_DROPDOWN = [
  {
    key: 0,
    label: 'None',
    value: ''
  },
  {
    key: 1,
    label: 'Status',
    value: 'status'
  },
  {
    key: 2,
    label: 'Assigned To',
    value: 'assignee'
  },
  {
    key: 3,
    label: 'Priority',
    value: 'priority'
  },
  {
    key: 4,
    label: 'Due Date',
    value: 'dueDate'
  }
]

export const ACTION_TYPES = [
  {
    key: 1,
    label: 'Task',
    value: 'task'
  },
  {
    key: 2,
    label: 'Care Gap',
    value: 'careGap'
  },
  {
    key: 3,
    label: 'Notification',
    value: 'notification'
  }
]

export const ACTION_TYPE_ICON_PROPS = new Map([
  [ACTION_TYPES[0].value, { icon: faListCheck, label: ACTION_TYPES[0].label }],
  [ACTION_TYPES[1].value, { icon: faFileHeart, label: ACTION_TYPES[1].label }],
  [ACTION_TYPES[2].value, { icon: faBell, label: ACTION_TYPES[2].label }]
])

export const ACTION_TYPES_WITH_ALL = addAllValue({
  list: [ACTION_TYPES[0], ACTION_TYPES[2]],
  convertList: false
})

export const PATIENT_REQUESTED_APPOINTMENTS_DROPDOWN = [
  {
    key: 0,
    label: 'Requested',
    value: 1
  },
  {
    key: 1,
    label: 'No request',
    value: 0
  }
]

export const PATIENT_REQUESTED_APPOINTMENTS_DROPDOWN_WITH_ALL = addAllValue({
  list: PATIENT_REQUESTED_APPOINTMENTS_DROPDOWN,
  convertList: false
})

export const ACTION_STATUSES = new Map([
  [
    0,
    {
      title: '-',
      className: '',
      value: 0
    }
  ],
  [
    1,
    {
      title: 'New',
      className: 'status-new',
      value: 1
    }
  ],
  [
    2,
    {
      title: 'In progress',
      className: 'status-in-progress',
      value: 2
    }
  ],
  [
    3,
    {
      title: 'Closed',
      className: 'status-closed',
      value: 3
    }
  ]
])

export const ACTION_PRIORITIES = new Map([
  [
    0,
    {
      title: '-',
      className: 'priority-none',
      value: 0
    }
  ],
  [
    1,
    {
      title: 'Low',
      className: 'priority-low',
      value: 1
    }
  ],
  [
    2,
    {
      title: 'Medium',
      className: 'priority-medium',
      value: 2
    }
  ],
  [
    3,
    {
      title: 'High',
      className: 'priority-high',
      value: 3
    }
  ]
])

export const ACTION_PRIORITIES_DROPDOWN = Array.from(ACTION_PRIORITIES)
  .map(([key, { title, value }]) => ({
    key,
    label: title,
    value
  }))
  .filter(({ key }) => key !== 0)

export const ACTION_PRIORITIES_DROPDOWN_WITH_ALL = addAllValue({
  list: ACTION_PRIORITIES_DROPDOWN,
  convertList: false
})

// page logger filter schema (covers both the standalone Action Hub and the
// patient-scoped Action page; fields absent for a given mode are skipped)
export const ACTION_HUB_LOG_SCHEMA: FilterFieldSchema[] = [
  { id: GROUP_BY, name: 'Group by', type: 'select' },
  { id: ACTION_TYPE, name: 'Action type', type: 'select' },
  { id: TASK_TYPE, name: 'Task type', type: 'select' },
  { id: CARE_GAP_TYPE, name: 'Care Gap type', type: 'select' },
  { id: NOTIFICATION_TYPE, name: 'Notification type', type: 'select' },
  { id: CASE_MANAGER_UUID, name: 'Case Manager', type: 'select' },
  { id: FIRST_NAME, name: 'Patient First Name', type: 'text' },
  { id: LAST_NAME, name: 'Patient Last Name', type: 'text' },
  { id: PATIENT_ID, name: 'Patient ID', type: 'text' },
  { id: `${GROUP}.${UUID}`, name: 'Group', type: 'select' },
  { id: REFERRAL_SOURCE, name: 'Referral Source', type: 'select' },
  { id: STATUS, name: 'Status', type: 'select' },
  { id: PRIORITY_FIELD_NAME, name: 'Priority', type: 'select' },
  { id: CREATED_BY, name: 'Created by', type: 'select' },
  { id: MENTIONED_USERS, name: 'Watched by', type: 'select' },
  { id: ASSIGNEE, name: 'Assigned to', type: 'select' },
  { id: COMPANY_DIVISION, name: 'Organization', type: 'select' },
  { id: PROVIDER, name: 'Provider', type: 'select' },
  { id: 'clinician', name: 'Clinician', type: 'select' },
  { id: FACILITY, name: 'Facility', type: 'select' },
  { id: PAYER, name: 'Payer', type: 'select' },
  { id: CREATED_DATE_FROM, name: 'Created date from', type: 'date' },
  { id: CREATED_DATE_TO, name: 'Created date to', type: 'date' },
  {
    id: LAST_ACTIVITY_DATE_FROM,
    name: 'Last activity date from',
    type: 'date'
  },
  { id: LAST_ACTIVITY_DATE_TO, name: 'Last activity date to', type: 'date' },
  { id: DUE_DATE_FROM, name: 'Due date from', type: 'date' },
  { id: DUE_DATE_TO, name: 'Due date to', type: 'date' },
  {
    id: PATIENT_REQUESTED_APPOINTMENT,
    name: 'Patient Requested Appointment',
    type: 'select'
  },
  { id: IS_NO_DUE_DATE, name: 'No due date', type: 'select' },
  { id: IS_RPM_BILLABLE, name: 'Rpm Billable', type: 'select' },
  { id: INSURANCE_TYPE_UUID, name: 'Insurance', type: 'select' }
]

export const CARE_GAP_CUSTOM_FIELD_TYPES = new Map([
  [1, { name: 'input', value: 1 }],
  [2, { name: 'checkbox', value: 2 }],
  [3, { name: 'datePicker', value: 3 }],
  [4, { name: 'select', value: 4 }],
  [5, { name: 'treeSelect', value: 5 }],
  [6, { name: 'file', value: 6 }]
])
